import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  pipes,
  type PipeCard,
  type PipeCardFilters,
  type PipeDetail,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { cn } from '@/shared/lib/cn';
import { formatDateTime, formatMoney } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody } from '@/shared/ui/Card';
import { Field } from '@/shared/ui/Field';
import { Icon } from '@/shared/ui/Icon';
import { Kanban, type KanbanColumn } from '@/shared/ui/Kanban';
import { Modal } from '@/shared/ui/Modal';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Select } from '@/shared/ui/Select';
import { statusLabel } from '@/shared/ui/statusLabels';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import {
  abreCartaoNoQuadro,
  podeMoverNaEsteira,
  tituloDoCartao,
  tomDaColuna,
} from './cartao';
import { PipeCardDrawer } from './PipeCardDrawer';
import { PipeMoveDialog, type PipeMoveTarget } from './PipeMoveDialog';
import { LimitReviewRequestDialog, PipeOpenCardDialog } from './PipeOpenCardDialog';

/** Cartoes de cada coluna por pagina ("Ver mais" traz a proxima). */
const porPagina = 20;

const portes = [
  { value: 'mei', label: 'MEI' },
  { value: 'me', label: 'ME' },
  { value: 'epp', label: 'EPP' },
  { value: 'medio', label: 'Medio' },
  { value: 'grande', label: 'Grande' },
];

interface Filtros {
  q: string;
  priority_id: string;
  owner_member_id: string;
  company_size: string;
  credit_class: string;
  overdue: boolean;
}

const semFiltro: Filtros = {
  q: '',
  priority_id: '',
  owner_member_id: '',
  company_size: '',
  credit_class: '',
  overdue: false,
};

/**
 * Quadro de uma esteira (/kanban/pipe/:id do OnPoint).
 *
 * As colunas vem do servidor, na ordem da esteira, com o contador de cada uma e o
 * "Ver mais" da coluna paginada. O cartao abre a gaveta sem sair do quadro. O
 * arrasto so oferece os destinos das regras de navegacao da esteira; o movimento
 * pede confirmacao, com motivo e justificativa, e quem decide e o servidor: a
 * coluna ligada a uma etapa da operacao passa pela transicao da operacao.
 */
export function PipeBoardPage() {
  const { pipeId = '' } = useParams();
  const { can } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  // A busca em todas as esteiras (PipesPage) chega com `q` e `cartao` no endereco: o
  // quadro abre com a busca aplicada e o cartao achado aberto.
  const [doEndereco] = useSearchParams();
  const [filtros, setFiltros] = useState<Filtros>(() => ({
    ...semFiltro,
    q: doEndereco.get('q') ?? '',
  }));
  const [mostrandoFiltros, setMostrandoFiltros] = useState(() => doEndereco.has('q'));
  const [aberto, setAberto] = useState<string | null>(() => doEndereco.get('cartao'));
  const [dialogo, setDialogo] = useState<'abrir' | 'revisao' | 'envio' | null>(null);
  const [movendo, setMovendo] = useState<{
    cardId: string;
    alvo: PipeMoveTarget;
  } | null>(null);
  // Paginas a mais carregadas pelo "Ver mais", por coluna, presas aos filtros em que
  // foram carregadas: filtro novo recomeca a paginacao sem efeito colateral.
  const [mais, setMais] = useState<{
    chave: string;
    porColuna: Record<string, PipeCard[]>;
  }>({ chave: '', porColuna: {} });

  const podeVer = can('esteiras.esteira.view');

  const lista = useQuery({
    queryKey: ['pipes'],
    queryFn: ({ signal }) => pipes.list(signal),
    enabled: podeVer,
  });
  const esteira = useQuery({
    queryKey: ['pipes', pipeId],
    queryFn: ({ signal }) => pipes.get(pipeId, signal),
    enabled: podeVer && pipeId !== '',
  });

  const parametros: PipeCardFilters = useMemo(
    () => ({
      q: filtros.q,
      priority_id: filtros.priority_id,
      owner_member_id: filtros.owner_member_id,
      company_size: filtros.company_size,
      credit_class: filtros.credit_class,
      overdue: filtros.overdue,
      limit: porPagina,
    }),
    [filtros],
  );

  const cartoes = useQuery({
    queryKey: ['pipes', pipeId, 'cards', parametros],
    queryFn: ({ signal }) => pipes.cards(pipeId, parametros, signal),
    enabled: podeVer && pipeId !== '',
  });
  const prioridades = useQuery({
    queryKey: ['pipes', 'priorities'],
    queryFn: ({ signal }) => pipes.priorities(true, signal),
    enabled: podeVer,
    staleTime: 5 * 60_000,
  });
  const responsaveis = useQuery({
    queryKey: ['pipes', pipeId, 'owners'],
    queryFn: ({ signal }) => pipes.owners(pipeId, signal),
    enabled: podeVer && pipeId !== '',
    staleTime: 5 * 60_000,
  });
  const dominios = useQuery({
    queryKey: ['pipes', 'domains'],
    queryFn: ({ signal }) => pipes.engineDomains(signal),
    enabled: podeVer,
    staleTime: 30 * 60_000,
  });

  const chaveDosFiltros = `${pipeId}:${JSON.stringify(parametros)}`;
  const maisPorColuna = mais.chave === chaveDosFiltros ? mais.porColuna : {};

  const totais = useMemo(() => {
    const out: Record<string, number> = {};
    const colunas = (cartoes.data?.meta.columns ?? []) as {
      step_id: string;
      total: number;
    }[];
    for (const c of colunas) out[c.step_id] = c.total;
    return out;
  }, [cartoes.data]);

  if (!podeVer) {
    return (
      <div className="flex flex-col gap-5">
        <PageHeader title="Esteiras" />
        <Alert tone="info" title="Esteiras indisponiveis para o seu acesso">
          O seu perfil nao inclui a visualizacao das esteiras.
        </Alert>
      </div>
    );
  }

  if (esteira.isError) {
    return (
      <div className="flex flex-col gap-5">
        <PageHeader title="Esteira" />
        <Alert tone="danger" title="Nao foi possivel abrir a esteira">
          <div className="flex flex-col gap-3">
            <p>{esteira.error.message}</p>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => void navigate('/esteiras')}
            >
              Voltar para as esteiras
            </Button>
          </div>
        </Alert>
      </div>
    );
  }

  const detalhe = esteira.data;
  const itens = [
    ...(cartoes.data?.items ?? []),
    ...Object.values(maisPorColuna).flat(),
  ];
  const colunas: KanbanColumn[] = (detalhe?.steps ?? []).map((step) => ({
    key: step.id,
    label: step.name,
    tone: tomDaColuna(step.type),
  }));

  function destinos(cartao: PipeCard, pipe: PipeDetail | undefined): string[] {
    if (!pipe || cartao.is_closed) return [];
    return pipe.transitions
      .filter((t) => t.from_step_id === cartao.step.id)
      .map((t) => t.to_step_id);
  }

  async function verMais(stepId: string) {
    const carregados = itens.filter((c) => c.step.id === stepId).length;
    try {
      const pagina = await pipes.cards(pipeId, {
        ...parametros,
        step_id: stepId,
        offset: carregados,
        limit: porPagina,
      });
      setMais({
        chave: chaveDosFiltros,
        porColuna: {
          ...maisPorColuna,
          [stepId]: [...(maisPorColuna[stepId] ?? []), ...pagina.items],
        },
      });
    } catch (erro) {
      toast.error(
        erro instanceof Error
          ? erro.message
          : 'Nao foi possivel carregar mais cartoes.',
      );
    }
  }

  const prioridadesAtivas = (prioridades.data ?? []).filter((p) => p.active);

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={detalhe?.name ?? 'Esteira'}
        description="Cartoes por coluna. Arraste para mover, ou abra o cartao para ver os dados da cotacao."
        actions={
          <div className="flex flex-wrap items-end gap-2">
            <Select
              label="Lista de esteiras"
              className="h-9"
              options={(lista.data ?? []).map((p) => ({ value: p.id, label: p.name }))}
              value={pipeId}
              onChange={(event) => void navigate(`/esteiras/${event.target.value}`)}
            />
            <Button
              variant="secondary"
              aria-expanded={mostrandoFiltros}
              onClick={() => setMostrandoFiltros((v) => !v)}
            >
              <Icon name="layers" size={15} />
              Filtros
            </Button>
            {abreCartaoNoQuadro(detalhe) && can('esteiras.cartao.create') ? (
              <Button variant="secondary" onClick={() => setDialogo('abrir')}>
                Abrir cartao
              </Button>
            ) : null}
            {detalhe?.key === 'credito' && can('credito.revisao_limite.create') ? (
              <Button variant="secondary" onClick={() => setDialogo('revisao')}>
                Pedir revisao de limite
              </Button>
            ) : null}
            {can('esteiras.envio.create') ? (
              <Button variant="secondary" onClick={() => setDialogo('envio')}>
                <Icon name="send" size={15} />
                Enviar documentos ao corretor
              </Button>
            ) : null}
          </div>
        }
      />

      {detalhe?.engine_lane ? (
        <RaiaDoMotor pipeId={pipeId} ligada={detalhe.engine_cards_enabled ?? true} />
      ) : null}

      {mostrandoFiltros ? (
        <Card>
          <CardBody className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <Field
              label="Buscar"
              value={filtros.q}
              placeholder="Cotacao, tomador ou corretora"
              onChange={(event) => setFiltros({ ...filtros, q: event.target.value })}
            />
            <Select
              label="Prioridade"
              placeholder="Todas"
              options={prioridadesAtivas.map((p) => ({ value: p.id, label: p.name }))}
              value={filtros.priority_id}
              onChange={(event) =>
                setFiltros({ ...filtros, priority_id: event.target.value })
              }
            />
            <Select
              label="Responsavel"
              placeholder="Todos"
              options={(responsaveis.data ?? []).map((o) => ({
                value: o.member_id,
                label: o.name,
              }))}
              value={filtros.owner_member_id}
              onChange={(event) =>
                setFiltros({ ...filtros, owner_member_id: event.target.value })
              }
            />
            <Select
              label="Porte"
              placeholder="Todos"
              options={portes}
              value={filtros.company_size}
              onChange={(event) =>
                setFiltros({ ...filtros, company_size: event.target.value })
              }
            />
            <Select
              label="Classe"
              placeholder="Todas"
              options={(dominios.data?.classes ?? []).map((c) => ({
                value: c,
                label: c,
              }))}
              value={filtros.credit_class}
              onChange={(event) =>
                setFiltros({ ...filtros, credit_class: event.target.value })
              }
            />
            <div className="flex flex-col justify-end gap-2">
              <label className="flex h-9 cursor-pointer items-center gap-2 text-xs text-ink-600">
                <input
                  type="checkbox"
                  checked={filtros.overdue}
                  onChange={(event) =>
                    setFiltros({ ...filtros, overdue: event.target.checked })
                  }
                  className="size-4 rounded border-line-strong text-brand-500 focus:ring-brand-500/25"
                />
                Somente prazo vencido
              </label>
              <Button size="sm" variant="ghost" onClick={() => setFiltros(semFiltro)}>
                Limpar filtro
              </Button>
            </div>
          </CardBody>
        </Card>
      ) : null}

      {cartoes.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar os cartoes">
          {cartoes.error.message}
        </Alert>
      ) : null}

      <Kanban
        columns={colunas}
        items={itens}
        keyOf={(cartao) => cartao.id}
        columnOf={(cartao) => cartao.step.id}
        targetsOf={(cartao) => destinos(cartao, detalhe)}
        canMove={podeMoverNaEsteira(can, detalhe)}
        onMove={(cartao, coluna) => {
          const step = detalhe?.steps.find((s) => s.id === coluna);
          if (!step) return;
          setMovendo({
            cardId: cartao.id,
            alvo: {
              stepId: step.id,
              stepName: step.name,
              operationLabel: step.operation_stage
                ? `levar a operacao para ${statusLabel('operacao', step.operation_stage).toLowerCase()}`
                : '',
              requiresReason:
                step.operation_stage === 'declinada' || step.justification.is_required,
              justification: step.justification,
            },
          });
        }}
        onOpen={(cartao) => setAberto(cartao.id)}
        loading={esteira.isPending || cartoes.isPending}
        loadingLabel="Carregando cartoes"
        emptyLabel="Nenhum cartao"
        totalOf={(coluna) => totais[coluna.key] ?? 0}
        countLabel={(total, coluna) => `${total} cartoes em ${coluna.label}`}
        footerOf={(coluna) => {
          const exibidos = itens.filter((c) => c.step.id === coluna.key).length;
          const total = totais[coluna.key] ?? 0;
          return exibidos < total ? (
            <Button
              size="sm"
              variant="ghost"
              className="w-full"
              onClick={() => void verMais(coluna.key)}
            >
              Ver mais ({exibidos} / {total})
            </Button>
          ) : null;
        }}
        renderCard={(cartao) => <CartaoDoQuadro cartao={cartao} />}
      />

      <p className="text-2xs text-ink-500">
        <Link to="/esteiras" className="font-medium text-brand-700 hover:underline">
          Todas as esteiras
        </Link>
      </p>

      <PipeCardDrawer
        cardId={aberto}
        pipeId={pipeId}
        owners={responsaveis.data ?? []}
        priorities={prioridades.data ?? []}
        onClose={() => setAberto(null)}
      />

      {movendo ? (
        <PipeMoveDialog
          cardId={movendo.cardId}
          pipeId={pipeId}
          target={movendo.alvo}
          onClose={() => setMovendo(null)}
        />
      ) : null}

      {detalhe && abreCartaoNoQuadro(detalhe) ? (
        <PipeOpenCardDialog
          key={detalhe.id}
          pipe={detalhe}
          open={dialogo === 'abrir'}
          onClose={() => setDialogo(null)}
          onOpened={(cartao) => setAberto(cartao.id)}
        />
      ) : null}

      <LimitReviewRequestDialog
        open={dialogo === 'revisao'}
        onClose={() => setDialogo(null)}
      />

      <EnvioEmLote
        pipeId={pipeId}
        open={dialogo === 'envio'}
        onClose={() => setDialogo(null)}
      />
    </div>
  );
}

/**
 * Caixa "Abrir cartao pelas raias do motor" das esteiras Credito, PEP e Subscricao: a
 * regra do motor que manda o caso para a raia abre o cartao da cotacao nesta esteira,
 * sem decidir nada. A empresa desliga por esteira.
 */
function RaiaDoMotor({ pipeId, ligada }: { pipeId: string; ligada: boolean }) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const gravar = useMutation({
    mutationFn: (valor: boolean) =>
      pipes.updateSettings(pipeId, { engine_cards_enabled: valor }),
    onSuccess: (esteira) => {
      toast.success(
        esteira.engine_cards_enabled
          ? 'A raia do motor volta a abrir cartao nesta esteira.'
          : 'A raia do motor deixa de abrir cartao nesta esteira.',
      );
      void queryClient.invalidateQueries({ queryKey: ['pipes'] });
    },
    onError: (error: Error) => toast.error(error.message),
  });
  return (
    <label className="flex cursor-pointer items-start gap-2 text-xs text-ink-600">
      <input
        type="checkbox"
        checked={ligada}
        disabled={!can('esteiras.configuracao.update') || gravar.isPending}
        onChange={(event) => gravar.mutate(event.target.checked)}
        className="mt-0.5 size-4 rounded border-line-strong text-brand-500 focus:ring-brand-500/25"
      />
      <span>
        Abrir cartao pelas raias do motor
        <span className="block text-2xs text-ink-500">
          A regra do motor que manda o caso para esta raia abre o cartao da cotacao, com
          a regra em Regra Reprovada. Abrir o cartao nao decide nada.
        </span>
      </span>
    </label>
  );
}

/**
 * "Enviar documentos ao corretor" do quadro: manda, de uma vez, os documentos
 * pendentes dos cartoes abertos da esteira. O cartao sem corretora ou sem documento
 * pendente fica de fora, com o motivo.
 */
function EnvioEmLote({
  pipeId,
  open,
  onClose,
}: {
  pipeId: string;
  open: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [nota, setNota] = useState('');
  const enviar = useMutation({
    mutationFn: () =>
      pipes.sendPipeDocumentRequests(pipeId, nota.trim() ? { note: nota.trim() } : {}),
    onSuccess: (lote) => {
      toast.success(
        `${lote.sent.length} cartao(oes) enviado(s) ao corretor; ${lote.skipped.length} sem envio.`,
      );
      void queryClient.invalidateQueries({ queryKey: ['pipe-cards'] });
      fechar();
    },
  });
  function fechar() {
    setNota('');
    enviar.reset();
    onClose();
  }
  return (
    <Modal
      open={open}
      onClose={fechar}
      title="Enviar documentos ao corretor"
      description="Cada cartao aberto desta esteira envia a sua corretora a lista dos documentos ainda pendentes. O aviso por e-mail depende de regra da seguradora e nao e mandado."
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={fechar}>
            Cancelar
          </Button>
          <Button loading={enviar.isPending} onClick={() => enviar.mutate()}>
            Enviar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {enviar.isError ? (
          <Alert tone="danger" title="Nao foi possivel enviar">
            {enviar.error.message}
          </Alert>
        ) : null}
        <Textarea
          label="Comentario do envio"
          value={nota}
          maxLength={2000}
          hint="Opcional. Vai junto com a lista de cada cartao."
          onChange={(event) => setNota(event.target.value)}
        />
      </div>
    </Modal>
  );
}

/**
 * O cartao do quadro: titulo, Tomador, Corretora, Modalidade e prioridade. O cartao
 * que nao e de cotacao mostra o tomador e a corretora do pedido, e o limite pretendido
 * na revisao de limite.
 */
function CartaoDoQuadro({ cartao }: { cartao: PipeCard }) {
  const o = cartao.operation;
  const s = cartao.subject;
  const temPrazo = Boolean(cartao.due_at) && !cartao.is_closed;
  const temRodape =
    cartao.is_overdue || cartao.is_closed || Boolean(cartao.owner_label) || temPrazo;
  return (
    <>
      <div className="flex flex-wrap items-start justify-between gap-x-2 gap-y-1">
        {/* O numero da cotacao tem dezesseis digitos: na coluna estreita a letra menor
            evita que ele quebre ao meio. */}
        <p className="min-w-0 text-xs leading-snug font-semibold wrap-anywhere text-ink-900 @max-[10rem]:text-2xs @max-[10rem]:tracking-tight">
          {tituloDoCartao(cartao)}
        </p>
        {cartao.priority ? (
          <Badge
            tone={
              cartao.priority.level >= 3
                ? 'danger'
                : cartao.priority.level >= 2
                  ? 'warning'
                  : 'neutral'
            }
          >
            {cartao.priority.name}
          </Badge>
        ) : null}
      </div>
      <div className="mt-1.5 flex flex-col gap-0.5">
        {o ? (
          <>
            <CampoDoCartao rotulo="Tomador" principal>
              {o.policyholder.label}
            </CampoDoCartao>
            <CampoDoCartao rotulo="Corretora">
              {o.broker?.label ?? 'Operacao direta'}
            </CampoDoCartao>
            <CampoDoCartao rotulo="Modalidade">{o.modality}</CampoDoCartao>
          </>
        ) : s ? (
          <>
            <CampoDoCartao rotulo="Tomador" principal>
              {s.policyholder.label}
            </CampoDoCartao>
            {s.broker ? (
              <CampoDoCartao rotulo="Corretora">{s.broker.label}</CampoDoCartao>
            ) : null}
            {s.requested_amount ? (
              <CampoDoCartao rotulo="Limite pretendido">
                {formatMoney(s.requested_amount)}
              </CampoDoCartao>
            ) : null}
            {s.status ? (
              <CampoDoCartao rotulo="Pedido">
                {statusLabel('pedidoCartao', s.status)}
              </CampoDoCartao>
            ) : null}
          </>
        ) : null}
      </div>
      {temRodape ? (
        <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-line pt-1.5">
          {cartao.is_overdue ? <Badge tone="danger">Prazo vencido</Badge> : null}
          {cartao.is_closed ? <Badge tone="neutral">Encerrado</Badge> : null}
          {cartao.owner_label ? (
            <span className="flex max-w-full min-w-0 items-center gap-1 text-2xs text-ink-500">
              <Icon name="users" size={11} className="shrink-0" />
              <span className="truncate">{cartao.owner_label}</span>
            </span>
          ) : null}
          {cartao.due_at && !cartao.is_closed ? (
            <span className="text-2xs text-ink-500">
              Prazo {formatDateTime(cartao.due_at)}
            </span>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

/**
 * Linha do cartao. Na coluna estreita o rotulo sai da tela e fica para o leitor de
 * tela: a ordem das linhas e sempre a mesma, e o valor cabe mais inteiro. A linha
 * principal, que identifica o cartao, ainda pode ocupar duas linhas ali.
 */
function CampoDoCartao({
  rotulo,
  principal,
  children,
}: {
  rotulo: string;
  principal?: boolean;
  children: string;
}) {
  return (
    <p
      title={children}
      className={cn(
        'truncate text-2xs text-ink-700',
        principal &&
          'font-medium @max-[12rem]:line-clamp-2 @max-[12rem]:whitespace-normal',
      )}
    >
      <span className="font-medium text-ink-500 @max-[12rem]:sr-only">{rotulo}:</span>{' '}
      {children}
    </p>
  );
}
